import { z } from 'zod';
import {
  type FunctionToolExecutionOptions,
  type Tool,
  tool,
  toolResult,
} from '../tool';
import { formatTask, formatTaskList } from './format';
import {
  TASK_CREATE_DESCRIPTION,
  TASK_GET_DESCRIPTION,
  TASK_LIST_DESCRIPTION,
  TASK_UPDATE_DESCRIPTION,
} from './prompts';
import {
  type TaskLifecycleHook,
  type TaskListState,
  type TaskListStore,
  type TaskRecord,
} from './types';

const TaskCreateInputSchema = z
  .object({
    subject: z.string().min(1).describe('任务的简短标题'),
    description: z.string().min(1).describe('需要完成的事项'),
    activeForm: z
      .string()
      .nullable()
      .optional()
      .describe(
        '进行中时在加载动画中显示的现在进行时形式（例如 "Running tests"',
      ),
    metadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe('附加到任务的任意元数据'),
  })
  .strict();

const TaskGetInputSchema = z
  .object({
    taskId: z.string().min(1).describe('要获取的任务的 ID'),
  })
  .strict();

const TaskUpdateInputSchema = z
  .object({
    taskId: z.string().describe('要更新的任务 ID'),
    subject: z.string().nullable().optional().describe('任务的新标题'),
    description: z.string().nullable().optional().describe('任务的新描述'),
    activeForm: z
      .string()
      .nullable()
      .optional()
      .describe(
        '进行中时在加载动画中显示的现在进行时形式（例如 "Running tests"）',
      ),
    status: z
      .enum(['pending', 'in_progress', 'completed', 'deleted'])
      .nullable()
      .optional()
      .describe('任务的新状态'),
    metadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe('要合并到任务中的元数据， 将某个键设为null即可删除它'),
    blockedBy: z
      .array(z.string().min(1))
      .nullable()
      .optional()
      .describe('阻塞此任务的任务 ID 列表。传 null 或空数组表示清空依赖'),
  })
  .strict();

const TaskListInputSchema = z.object({}).strict();

const TASK_LIST_TOOL_EXECUTION = {
  mode: 'serial',
  concurrencyKey: 'lyra:task-list',
} satisfies FunctionToolExecutionOptions;

type TaskUpdateInput = z.infer<typeof TaskUpdateInputSchema>;

export type TaskListToolsOptions = {
  store: TaskListStore;
  getTaskListId: () => string | Promise<string>;
  onTaskCreated?: TaskLifecycleHook;
  onTaskCompleted?: TaskLifecycleHook;
};

/**
 * Wrap a model-visible text output with the structured task-list snapshot on the
 * UI-only `display` channel. Every task tool returns the full current list so the
 * client can render an always-up-to-date view without replaying the call history.
 */
async function withTaskListDisplay(
  options: TaskListToolsOptions,
  taskListId: string,
  output: string,
) {
  const tasks = await options.store.transaction(taskListId, (state) =>
    state.list(),
  );
  return toolResult({ output, display: { kind: 'task_list', tasks } });
}

export function createTaskListTools(
  options: TaskListToolsOptions,
): Tool<any>[] {
  return [
    tool({
      name: 'TaskCreate',
      description: TASK_CREATE_DESCRIPTION,
      execution: TASK_LIST_TOOL_EXECUTION,
      parameters: TaskCreateInputSchema,
      execute: async (input) => {
        const taskListId = await options.getTaskListId();
        const task = await options.store.transaction(taskListId, (state) =>
          state.create({
            subject: input.subject,
            description: input.description,
            ...(input.activeForm ? { activeForm: input.activeForm } : {}),
            ...(input.metadata ? { metadata: input.metadata } : {}),
          }),
        );
        const hookMessage = await runLifecycleHook(options.onTaskCreated, task);
        if (hookMessage) {
          await options.store.transaction(taskListId, (state) => {
            state.delete(task.id);
          });
          return withTaskListDisplay(
            options,
            taskListId,
            `Task #${task.id} creation was blocked: ${hookMessage}`,
          );
        }
        return withTaskListDisplay(
          options,
          taskListId,
          `Task #${task.id} created successfully.\n${formatTask(task)}`,
        );
      },
    }),
    tool({
      name: 'TaskGet',
      description: TASK_GET_DESCRIPTION,
      execution: TASK_LIST_TOOL_EXECUTION,
      parameters: TaskGetInputSchema,
      execute: async ({ taskId }) => {
        const taskListId = await options.getTaskListId();
        const task = await options.store.transaction(taskListId, (state) =>
          state.get(taskId),
        );
        if (!task) {
          return withTaskListDisplay(
            options,
            taskListId,
            `Task #${taskId} does not exist.`,
          );
        }
        return withTaskListDisplay(options, taskListId, formatTask(task));
      },
    }),
    tool({
      name: 'TaskUpdate',
      description: TASK_UPDATE_DESCRIPTION,
      execution: TASK_LIST_TOOL_EXECUTION,
      parameters: TaskUpdateInputSchema,
      execute: async (input) => {
        const taskListId = await options.getTaskListId();
        if (input.status === 'completed') {
          const proposedTask = await options.store.transaction(
            taskListId,
            (state) => {
              const current = requireTask(state, input.taskId);
              return applyDirectTaskFields(current, input);
            },
          );
          const hookMessage = await runLifecycleHook(
            options.onTaskCompleted,
            proposedTask,
          );
          if (hookMessage) {
            return withTaskListDisplay(
              options,
              taskListId,
              `Task #${input.taskId} was not completed: ${hookMessage}`,
            );
          }
        }

        const output = await options.store.transaction(taskListId, (state) => {
          if (input.status === 'deleted') {
            deleteTaskAndReferences(state, input.taskId);
            return `Task #${input.taskId} deleted successfully.`;
          }

          const updated = applyTaskUpdate(state, input);
          return `Task #${updated.id} updated successfully.\n${formatTask(updated)}`;
        });
        return withTaskListDisplay(options, taskListId, output);
      },
    }),
    tool({
      name: 'TaskList',
      description: TASK_LIST_DESCRIPTION,
      execution: TASK_LIST_TOOL_EXECUTION,
      parameters: TaskListInputSchema,
      execute: async () => {
        const taskListId = await options.getTaskListId();
        const tasks = await options.store.transaction(taskListId, (state) =>
          state.list(),
        );
        return toolResult({
          output: formatTaskList(tasks),
          display: { kind: 'task_list', tasks },
        });
      },
    }),
  ];
}

function applyTaskUpdate(
  state: TaskListState,
  input: TaskUpdateInput,
): TaskRecord {
  let task = applyDirectTaskFields(requireTask(state, input.taskId), input);
  state.save(task);

  if (input.blockedBy !== undefined) {
    task = setBlockedBy(state, task, input.blockedBy ?? []);
  }

  return state.save(task);
}

function applyDirectTaskFields(
  task: TaskRecord,
  input: TaskUpdateInput,
): TaskRecord {
  const updated: TaskRecord = { ...task };
  if (input.subject !== undefined && input.subject !== null) {
    updated.subject = input.subject;
  }
  if (input.description !== undefined && input.description !== null) {
    updated.description = input.description;
  }
  if (input.activeForm !== undefined) {
    if (input.activeForm === null) {
      delete updated.activeForm;
    } else {
      updated.activeForm = input.activeForm;
    }
  }
  if (input.status && input.status !== 'deleted') {
    updated.status = input.status;
  }
  if (input.metadata !== undefined && input.metadata !== null) {
    const metadata = { ...(updated.metadata ?? {}) };
    for (const [key, value] of Object.entries(input.metadata)) {
      if (value === null) {
        delete metadata[key];
      } else {
        metadata[key] = value;
      }
    }
    if (Object.keys(metadata).length === 0) {
      delete updated.metadata;
    } else {
      updated.metadata = metadata;
    }
  }
  return updated;
}

function setBlockedBy(
  state: TaskListState,
  task: TaskRecord,
  nextIds: string[],
): TaskRecord {
  const next = normalizeDependencyIds(task.id, nextIds);
  const previous = new Set(task.blockedBy);
  const nextSet = new Set(next);

  for (const removedId of [...previous].filter((id) => !nextSet.has(id))) {
    const removedTask = state.get(removedId);
    if (removedTask) {
      removedTask.blocks = removedTask.blocks.filter((id) => id !== task.id);
      state.save(removedTask);
    }
  }

  for (const addedId of next) {
    const blocker = requireTask(state, addedId);
    if (!blocker.blocks.includes(task.id)) {
      blocker.blocks = [...blocker.blocks, task.id];
      state.save(blocker);
    }
  }

  task.blockedBy = next;
  return state.save(task);
}

function deleteTaskAndReferences(state: TaskListState, taskId: string): void {
  requireTask(state, taskId);
  for (const task of state.list()) {
    if (task.id === taskId) {
      continue;
    }
    const blocks = task.blocks.filter((id) => id !== taskId);
    const blockedBy = task.blockedBy.filter((id) => id !== taskId);
    if (
      blocks.length !== task.blocks.length ||
      blockedBy.length !== task.blockedBy.length
    ) {
      state.save({ ...task, blocks, blockedBy });
    }
  }
  state.delete(taskId);
}

function normalizeDependencyIds(taskId: string, taskIds: string[]): string[] {
  const normalized: string[] = [];
  for (const id of taskIds) {
    if (id === taskId) {
      throw new Error(`Task #${taskId} cannot depend on itself.`);
    }
    if (!normalized.includes(id)) {
      normalized.push(id);
    }
  }
  return normalized;
}

function requireTask(state: TaskListState, taskId: string): TaskRecord {
  const task = state.get(taskId);
  if (!task) {
    throw new Error(`Task #${taskId} does not exist.`);
  }
  return task;
}

async function runLifecycleHook(
  hook: TaskLifecycleHook | undefined,
  task: TaskRecord,
): Promise<string | null> {
  if (!hook) {
    return null;
  }
  try {
    const result = await hook(task);
    return typeof result === 'string' && result.trim() ? result : null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}
